import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  FileDownload,
  Edit,
  Save,
  Close,
  Delete,
  Add,
  RemoveCircleOutline,
  ContentCopy
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import accountService from '../services/accountService';
import billingService from '../services/billingService';
import siteService from '../services/siteService';
import { downloadAccountBillingCSV, downloadAccountReadingsCSV } from '../utils/downloadHelper';
import { useAuth } from '../context/AuthContext';

const BillingReports = () => {
  const { user } = useAuth();
  const canManageAccounts = user?.role === 'admin' || user?.role === 'operator';
  const canDeleteAccounts = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [billingTemplates, setBillingTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountTemplate, setNewAccountTemplate] = useState('billing1');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [allSites, setAllSites] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountSites, setSelectedAccountSites] = useState([]);
  const [selectedAccountSiteIds, setSelectedAccountSiteIds] = useState([]);
  const [selectedAccountSitesLoading, setSelectedAccountSitesLoading] = useState(false);
  const [savingAccountSites, setSavingAccountSites] = useState(false);
  const [connectedSitesSearch, setConnectedSitesSearch] = useState('');
  const [selectedSiteToAdd, setSelectedSiteToAdd] = useState('');
  const [accountBillingStartDate, setAccountBillingStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [accountBillingEndDate, setAccountBillingEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [accountBillingLoading, setAccountBillingLoading] = useState(false);
  const [accountBillingSummary, setAccountBillingSummary] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Billing template management (admin)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateDialogMode, setTemplateDialogMode] = useState('create');
  const [templateForm, setTemplateForm] = useState(null);
  const [savingTemplateDef, setSavingTemplateDef] = useState(false);
  const [templateDeleteDialogOpen, setTemplateDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deletingTemplateDef, setDeletingTemplateDef] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceKey, setDuplicateSourceKey] = useState('');
  const [duplicateKey, setDuplicateKey] = useState('');
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicatingTemplate, setDuplicatingTemplate] = useState(false);

  const MODE_LABELS = {
    tiered: 'Standard (Tiered)',
    pastel_rounded_down: 'Pastel (Rounded Down)',
    basic_readings: 'Basic Readings'
  };

  const emptyTemplateForm = () => ({
    key: '',
    name: '',
    description: '',
    currency: 'ZAR',
    calculationMode: 'pastel_rounded_down',
    rates: {
      multiplier: 1,
      waterBasicCharge: 0,
      sewerBasicCharge: 0,
      sewerConsumptionRate: 0,
      waterTiers: [{ maxRangeLitres: 10000, rate: 0 }],
      pricingLevels: [{ threshold: 0, rate: 0, description: '' }]
    },
    exportConfig: {
      gdc: 'D', taxType: '0', taxAmount: '0.00', openItem: '', projectCode: '',
      contraAccount: '4020000', exchangeRate: '1.00', bankExchangeRate: '1.00',
      batchId: '0', discountTaxType: '0', discountAmount: '0.00'
    }
  });

  const normalizeSiteIds = (siteIds) => {
    if (!Array.isArray(siteIds)) return [];
    const normalized = siteIds
      .map((value) => Number.parseInt(String(value), 10))
      .filter((value) => Number.isInteger(value) && value > 0);
    return Array.from(new Set(normalized));
  };

  const loadSelectedAccountSites = async (accountId) => {
    setSelectedAccountSitesLoading(true);
    try {
      const sites = await accountService.fetchAccountSites(accountId);
      const normalizedSites = Array.isArray(sites) ? sites : [];
      setSelectedAccountSites(normalizedSites);
      setSelectedAccountSiteIds(normalizeSiteIds(normalizedSites.map((site) => site?.id)));
      return normalizedSites;
    } catch (loadError) {
      console.error('Error loading account sites:', loadError);
      setSelectedAccountSites([]);
      setSelectedAccountSiteIds([]);
      setError('Failed to load account sites');
      return [];
    } finally {
      setSelectedAccountSitesLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [accountsData, templates, sitesData] = await Promise.all([
          accountService.fetchAccounts(),
          billingService.fetchBillingTemplates(),
          siteService.fetchSites()
        ]);
        setAccounts(Array.isArray(accountsData) ? accountsData : []);
        setBillingTemplates(templates);
        setAllSites(Array.isArray(sitesData) ? sitesData : []);
        if (Array.isArray(templates) && templates.length > 0) {
          setNewAccountTemplate((prev) => prev || templates[0].id);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleRequestDeleteAccount = (account) => {
    if (!canDeleteAccounts) return;
    setAccountToDelete(account || null);
    setDeleteDialogOpen(true);
  };

  const handleCancelDeleteAccount = () => {
    if (deletingAccount) return;
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const handleConfirmDeleteAccount = async () => {
    if (!canDeleteAccounts || !accountToDelete?.id || deletingAccount) return;

    setDeletingAccount(true);
    try {
      await accountService.deleteAccount(accountToDelete.id);

      setAccounts((prev) => prev.filter((account) => account.id !== accountToDelete.id));
      if (selectedAccountId === accountToDelete.id) {
        setSelectedAccountId(null);
        setSelectedAccountSites([]);
        setAccountBillingSummary(null);
      }

      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      setError(null);
    } catch (deleteError) {
      console.error('Error deleting account:', deleteError);
      const apiError = deleteError?.response?.data?.error;
      setError(apiError || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleEditTemplate = (account) => {
    if (!canManageAccounts) return;
    setEditingTemplate(account.id);
    setSelectedTemplate(account.billingTemplateKey || 'billing1');
  };

  const handleSaveTemplate = async () => {
    if (!canManageAccounts) return;
    if (!editingTemplate) return;

    setSavingTemplate(true);
    try {
      await accountService.updateAccount(editingTemplate, {
        billingTemplateKey: selectedTemplate
      });
      
      // Update local state
      setAccounts(prev => prev.map(account => 
        account.id === editingTemplate 
          ? { ...account, billingTemplateKey: selectedTemplate }
          : account
      ));
      
      setEditingTemplate(null);
      setError(null);
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCancelEditTemplate = () => {
    setEditingTemplate(null);
    setSelectedTemplate('');
  };

  const handleCreateAccount = async () => {
    if (!canManageAccounts) return;
    const trimmedName = String(newAccountName || '').trim();
    if (!trimmedName) {
      setError('Account name is required');
      return;
    }

    setCreatingAccount(true);
    try {
      const created = await accountService.createAccount({
        name: trimmedName,
        billingTemplateKey: newAccountTemplate || 'billing1'
      });

      setAccounts((prev) => [...prev, created]);
      setNewAccountName('');
      setError(null);
    } catch (error) {
      console.error('Error creating account:', error);
      setError('Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleExportAccountCSV = async (accountId) => {
    try {
      const startDate = accountBillingStartDate || format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const endDate = accountBillingEndDate || format(endOfMonth(new Date()), 'yyyy-MM-dd');
      await downloadAccountBillingCSV(accountId, startDate, endDate);
      setError(null);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV');
    }
  };

  const handleDownloadReadingsCSV = async () => {
    if (!selectedAccountId || !accountBillingStartDate || !accountBillingEndDate) {
      setError('Select an account and a billing period first');
      return;
    }
    try {
      await downloadAccountReadingsCSV(selectedAccountId, accountBillingStartDate, accountBillingEndDate);
      setError(null);
    } catch (downloadError) {
      console.error('Error downloading meter readings CSV:', downloadError);
      setError('Failed to download meter readings CSV');
    }
  };

  const refreshTemplates = async () => {
    const templates = await billingService.fetchBillingTemplates();
    setBillingTemplates(Array.isArray(templates) ? templates : []);
  };

  const openCreateTemplateDialog = () => {
    setTemplateForm(emptyTemplateForm());
    setTemplateDialogMode('create');
    setTemplateDialogOpen(true);
  };

  const openEditTemplateDialog = async (templateKey) => {
    try {
      const detail = await billingService.fetchBillingTemplate(templateKey);
      const base = emptyTemplateForm();
      setTemplateForm({
        ...base,
        ...detail,
        key: detail.key || templateKey,
        rates: { ...base.rates, ...(detail.rates || {}) },
        exportConfig: { ...base.exportConfig, ...(detail.exportConfig || {}) }
      });
      setTemplateDialogMode('edit');
      setTemplateDialogOpen(true);
    } catch (fetchError) {
      console.error('Error loading template:', fetchError);
      setError('Failed to load billing template');
    }
  };

  const updateTemplateRatesField = (field, value) => {
    setTemplateForm((prev) => ({
      ...prev,
      rates: { ...prev.rates, [field]: value }
    }));
  };

  const updateTemplateTier = (listName, index, field, value) => {
    setTemplateForm((prev) => {
      const list = [...(prev.rates[listName] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, rates: { ...prev.rates, [listName]: list } };
    });
  };

  const addTemplateTier = (listName, defaults) => {
    setTemplateForm((prev) => ({
      ...prev,
      rates: { ...prev.rates, [listName]: [...(prev.rates[listName] || []), defaults] }
    }));
  };

  const removeTemplateTier = (listName, index) => {
    setTemplateForm((prev) => ({
      ...prev,
      rates: { ...prev.rates, [listName]: (prev.rates[listName] || []).filter((_, i) => i !== index) }
    }));
  };

  const handleSaveTemplateDef = async () => {
    if (!templateForm) return;
    if (!String(templateForm.name || '').trim()) {
      setError('Template name is required');
      return;
    }

    const mode = templateForm.calculationMode;
    const toNum = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
    const rates = mode === 'pastel_rounded_down'
      ? {
          multiplier: toNum(templateForm.rates.multiplier),
          waterBasicCharge: toNum(templateForm.rates.waterBasicCharge),
          sewerBasicCharge: toNum(templateForm.rates.sewerBasicCharge),
          sewerConsumptionRate: toNum(templateForm.rates.sewerConsumptionRate),
          waterTiers: (templateForm.rates.waterTiers || []).map((t) => ({
            maxRangeLitres: toNum(t.maxRangeLitres),
            rate: toNum(t.rate)
          }))
        }
      : mode === 'tiered'
        ? {
            pricingLevels: (templateForm.rates.pricingLevels || []).map((l) => ({
              threshold: toNum(l.threshold),
              rate: toNum(l.rate),
              description: l.description || ''
            }))
          }
        : null;

    const payload = {
      name: templateForm.name,
      description: templateForm.description,
      currency: templateForm.currency || 'ZAR',
      calculationMode: mode,
      rates,
      exportConfig: mode === 'pastel_rounded_down' ? templateForm.exportConfig : null
    };

    setSavingTemplateDef(true);
    try {
      if (templateDialogMode === 'create') {
        if (templateForm.key && String(templateForm.key).trim()) payload.key = String(templateForm.key).trim();
        await billingService.createBillingTemplate(payload);
      } else {
        await billingService.updateBillingTemplate(templateForm.key, payload);
      }
      await refreshTemplates();
      setTemplateDialogOpen(false);
      setTemplateForm(null);
      setError(null);
    } catch (saveError) {
      console.error('Error saving billing template:', saveError);
      setError(saveError?.response?.data?.error || saveError.message || 'Failed to save billing template');
    } finally {
      setSavingTemplateDef(false);
    }
  };

  const openDuplicateTemplateDialog = (template) => {
    setDuplicateSourceKey(template.id);
    setDuplicateKey(`${template.id}_copy`);
    setDuplicateName(`${template.name} (Copy)`);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateTemplate = async () => {
    if (!duplicateSourceKey) return;
    setDuplicatingTemplate(true);
    try {
      await billingService.duplicateBillingTemplate(duplicateSourceKey, {
        key: duplicateKey || undefined,
        name: duplicateName || undefined
      });
      await refreshTemplates();
      setDuplicateDialogOpen(false);
      setError(null);
    } catch (dupError) {
      console.error('Error duplicating template:', dupError);
      setError(dupError?.response?.data?.error || dupError.message || 'Failed to duplicate template');
    } finally {
      setDuplicatingTemplate(false);
    }
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!canDeleteAccounts || !templateToDelete?.id || deletingTemplateDef) return;
    setDeletingTemplateDef(true);
    try {
      await billingService.deleteBillingTemplate(templateToDelete.id);
      await refreshTemplates();
      setTemplateDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setError(null);
    } catch (delError) {
      console.error('Error deleting template:', delError);
      setError(delError?.response?.data?.error || delError.message || 'Failed to delete template');
    } finally {
      setDeletingTemplateDef(false);
    }
  };

  const handleSelectAccount = async (account) => {
    const accountId = account?.id;
    if (!accountId) return;

    if (selectedAccountId === accountId) {
      setSelectedAccountId(null);
      setSelectedAccountSites([]);
      setSelectedAccountSiteIds([]);
      setConnectedSitesSearch('');
      setAccountBillingSummary(null);
      return;
    }

    setConnectedSitesSearch('');
    setSelectedAccountId(accountId);
    setAccountBillingSummary(null);
    await loadSelectedAccountSites(accountId);
  };

  const saveAccountSites = async (siteIds) => {
    if (!canManageAccounts || !selectedAccountId || savingAccountSites) return null;

    const normalizedSiteIds = normalizeSiteIds(siteIds);
    setSavingAccountSites(true);
    try {
      await accountService.updateAccountSites(selectedAccountId, normalizedSiteIds);
      const refreshedSites = await loadSelectedAccountSites(selectedAccountId);

      setAccounts((prev) => prev.map((account) => (
        account.id === selectedAccountId
          ? { ...account, sites: refreshedSites }
          : account
      )));
      setError(null);
      return refreshedSites;
    } catch (saveError) {
      console.error('Error saving account sites:', saveError);
      setError('Failed to save account sites');
      return null;
    } finally {
      setSavingAccountSites(false);
    }
  };

  const handleAddSiteToAccount = async () => {
    const siteId = Number.parseInt(String(selectedSiteToAdd), 10);
    if (!Number.isInteger(siteId) || siteId <= 0) return;

    const nextSiteIds = normalizeSiteIds([...selectedAccountSiteIds, siteId]);
    setSelectedAccountSiteIds(nextSiteIds);
    const savedSites = await saveAccountSites(nextSiteIds);
    if (savedSites) {
      setSelectedSiteToAdd('');
    }
  };

  const handleRemoveSiteFromAccount = async (siteId) => {
    const normalizedSiteId = Number.parseInt(String(siteId), 10);
    if (!Number.isInteger(normalizedSiteId) || normalizedSiteId <= 0) return;

    const nextSiteIds = selectedAccountSiteIds.filter((id) => Number(id) !== normalizedSiteId);
    setSelectedAccountSiteIds(nextSiteIds);
    await saveAccountSites(nextSiteIds);
  };

  const handleGenerateAccountBilling = async () => {
    if (!selectedAccountId) return;
    if (!accountBillingStartDate || !accountBillingEndDate) {
      setError('Select a start and end date for account billing');
      return;
    }

    setAccountBillingLoading(true);
    try {
      const result = await billingService.calculateAccountBilling(selectedAccountId, {
        startDate: accountBillingStartDate,
        endDate: accountBillingEndDate
      });
      setAccountBillingSummary(result);
      setError(null);
    } catch (billingError) {
      console.error('Error generating account billing:', billingError);
      setError('Failed to generate consolidated account billing');
      setAccountBillingSummary(null);
    } finally {
      setAccountBillingLoading(false);
    }
  };

  const handleDownloadConsolidatedAccountCSV = async () => {
    if (!selectedAccountId) return;
    if (!accountBillingStartDate || !accountBillingEndDate) {
      setError('Select a start and end date for account billing');
      return;
    }

    try {
      await downloadAccountBillingCSV(
        selectedAccountId,
        accountBillingStartDate,
        accountBillingEndDate
      );
      setError(null);
    } catch (downloadError) {
      console.error('Error downloading consolidated account CSV:', downloadError);
      setError('Failed to download consolidated account billing CSV');
    }
  };

  const getTemplateName = (templateKey) => {
    const template = billingTemplates.find(t => t.id === templateKey);
    return template ? template.name : templateKey || 'Not Set';
  };

  const normalizedConnectedSitesSearch = String(connectedSitesSearch || '').trim().toLowerCase();
  const filteredSelectedAccountSites = selectedAccountSites.filter((site) => {
    if (!normalizedConnectedSitesSearch) return true;
    const searchable = [
      site?.description,
      site?.location,
      site?.id != null ? String(site.id) : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchable.includes(normalizedConnectedSitesSearch);
  });

  const availableSitesToAdd = allSites.filter((site) => {
    const siteId = Number.parseInt(String(site?.id), 10);
    return Number.isInteger(siteId) && siteId > 0 && !selectedAccountSiteIds.includes(siteId);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Accounts Billing Table */}
      <Card elevation={2}>
        <CardContent>
          {canManageAccounts && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Create Account
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="New Account Name"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="new-account-template-label">Billing Template</InputLabel>
                <Select
                  labelId="new-account-template-label"
                  value={newAccountTemplate}
                  label="Billing Template"
                  onChange={(e) => setNewAccountTemplate(e.target.value)}
                >
                  {billingTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateAccount}
                  disabled={creatingAccount}
                  startIcon={creatingAccount ? <CircularProgress size={16} color="inherit" /> : <Save />}
                >
                  Create Account
                </Button>
              </Box>
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="center">Sites</TableCell>
                  <TableCell align="center">Billing Template</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No accounts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <React.Fragment key={account.id}>
                      <TableRow
                        hover
                        selected={selectedAccountId === account.id}
                        onClick={() => handleSelectAccount(account)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {account.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{Array.isArray(account.sites) ? account.sites.length : 0}</TableCell>
                        <TableCell align="center">
                          {canManageAccounts && editingTemplate === account.id ? (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <Select
                                value={selectedTemplate}
                                onChange={(e) => setSelectedTemplate(e.target.value)}
                              >
                                {billingTemplates.map(template => (
                                  <MenuItem key={template.id} value={template.id}>
                                    {template.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip 
                              label={getTemplateName(account.billingTemplateKey)}
                              size="small"
                              color={account.billingTemplateKey ? 'success' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            {canManageAccounts && editingTemplate === account.id ? (
                              <>
                                <Tooltip title="Save">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveTemplate();
                                    }}
                                    disabled={savingTemplate}
                                  >
                                    {savingTemplate ? <CircularProgress size={20} /> : <Save />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelEditTemplate();
                                    }}
                                  >
                                    <Close />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                {canManageAccounts && (
                                  <Tooltip title="Edit Template">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTemplate(account);
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDeleteAccounts && (
                                  <Tooltip title="Delete Account">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestDeleteAccount(account);
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Export CSV">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExportAccountCSV(account.id);
                                    }}
                                  >
                                    <FileDownload />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedAccountId && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Account Details & Consolidated Billing
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Selected account: {accounts.find((a) => a.id === selectedAccountId)?.name || selectedAccountId}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Connected Sites
                </Typography>
                <TextField
                  size="small"
                  label="Search Connected Sites"
                  value={connectedSitesSearch}
                  onChange={(e) => setConnectedSitesSearch(e.target.value)}
                  sx={{ mb: 2, minWidth: 260, maxWidth: 420 }}
                />
                {canManageAccounts && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 280, maxWidth: 520, flex: 1 }}>
                      <InputLabel id="add-account-site-label">Add Site</InputLabel>
                      <Select
                        labelId="add-account-site-label"
                        value={selectedSiteToAdd}
                        label="Add Site"
                        onChange={(e) => setSelectedSiteToAdd(e.target.value)}
                        disabled={selectedAccountSitesLoading || savingAccountSites || availableSitesToAdd.length === 0}
                      >
                        {availableSitesToAdd.map((site) => (
                          <MenuItem key={site.id} value={String(site.id)}>
                            {site?.description || `Site ${site.id}`}
                            {site?.location ? ` (${site.location})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={handleAddSiteToAccount}
                      disabled={!selectedSiteToAdd || selectedAccountSitesLoading || savingAccountSites}
                      startIcon={<Add />}
                    >
                      Add Site
                    </Button>
                  </Box>
                )}
                {selectedAccountSitesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading sites...</Typography>
                  </Box>
                ) : selectedAccountSites.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No sites connected to this account.
                  </Typography>
                ) : filteredSelectedAccountSites.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No connected sites match your search.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(3, minmax(0, 1fr))'
                      },
                      gap: 1.25,
                      mb: 2
                    }}
                  >
                    {filteredSelectedAccountSites.map((site) => {
                      const siteDevices = Array.isArray(site?.siteDevices) ? site.siteDevices : [];
                      const totalDevices = siteDevices.length;

                      return (
                      <Card key={site.id} variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                          {canManageAccounts && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
                              <Tooltip title="Remove from account">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveSiteFromAccount(site.id)}
                                  disabled={savingAccountSites || selectedAccountSitesLoading}
                                >
                                  <RemoveCircleOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }} noWrap>
                            {site?.description || `Site ${site?.id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.2 }}>
                            {site?.location || '-'}
                          </Typography>

                          <Chip size="small" variant="outlined" label={`Devices: ${totalDevices}`} />
                        </CardContent>
                      </Card>
                    );
                    })}
                  </Box>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Consolidated Billing Period
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Start Date"
                    value={accountBillingStartDate}
                    onChange={(e) => setAccountBillingStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="End Date"
                    value={accountBillingEndDate}
                    onChange={(e) => setAccountBillingEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleGenerateAccountBilling}
                    disabled={accountBillingLoading || selectedAccountSitesLoading}
                  >
                    {accountBillingLoading ? 'Generating...' : 'Generate Billing (Account)'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownload />}
                    onClick={handleDownloadConsolidatedAccountCSV}
                    disabled={selectedAccountSitesLoading}
                  >
                    Download Consolidated CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownload />}
                    onClick={handleDownloadReadingsCSV}
                    disabled={selectedAccountSitesLoading}
                  >
                    Download Meter Readings CSV
                  </Button>
                </Box>

                {accountBillingSummary && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Sites: ${Number(accountBillingSummary.siteCount || selectedAccountSites.length || 0)}`} color="primary" variant="outlined" />
                    <Chip label={`Devices: ${Number(accountBillingSummary.deviceCount || 0)}`} color="primary" variant="outlined" />
                    <Chip label={`Records: ${Array.isArray(accountBillingSummary.records) ? accountBillingSummary.records.length : 0}`} color="primary" variant="outlined" />
                    <Chip label={`Total: ${Number(accountBillingSummary.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color="success" />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {canManageAccounts && (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Billing Templates
              </Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreateTemplateDialog}>
                New Template
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Template</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="center">Currency</TableCell>
                    <TableCell align="center">Accounts</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingTemplates.map((template) => (
                    <TableRow key={template.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{template.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{template.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={MODE_LABELS[template.calculationMode] || template.calculationMode || '-'} />
                      </TableCell>
                      <TableCell align="center">{template.currency || 'ZAR'}</TableCell>
                      <TableCell align="center">{template.accountCount ?? 0}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Edit Template">
                            <IconButton size="small" color="primary" onClick={() => openEditTemplateDialog(template.id)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate Template">
                            <IconButton size="small" color="primary" onClick={() => openDuplicateTemplateDialog(template)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canDeleteAccounts && (
                            <Tooltip title={(template.accountCount || 0) > 0 ? 'In use by accounts' : 'Delete Template'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={(template.accountCount || 0) > 0}
                                  onClick={() => { setTemplateToDelete(template); setTemplateDeleteDialogOpen(true); }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Billing Template create/edit dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => !savingTemplateDef && setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{templateDialogMode === 'create' ? 'New Billing Template' : `Edit: ${templateForm?.name || ''}`}</DialogTitle>
        <DialogContent dividers>
          {templateForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {templateDialogMode === 'create' && (
                  <TextField
                    size="small"
                    label="Template Key (optional)"
                    value={templateForm.key}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, key: e.target.value }))}
                    helperText="Leave blank to auto-generate"
                    sx={{ minWidth: 220 }}
                  />
                )}
                <TextField
                  size="small"
                  label="Billing Portfolio Name"
                  required
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
                  sx={{ minWidth: 280, flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Currency"
                  value={templateForm.currency}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, currency: e.target.value }))}
                  sx={{ width: 120 }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="template-mode-label">Billing Export Type</InputLabel>
                  <Select
                    labelId="template-mode-label"
                    label="Billing Export Type"
                    value={templateForm.calculationMode}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, calculationMode: e.target.value }))}
                  >
                    <MenuItem value="pastel_rounded_down">Pastel (Rounded Down)</MenuItem>
                    <MenuItem value="tiered">Standard (Tiered)</MenuItem>
                    <MenuItem value="basic_readings">Basic Readings</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField
                size="small"
                label="Description"
                value={templateForm.description || ''}
                onChange={(e) => setTemplateForm((p) => ({ ...p, description: e.target.value }))}
                fullWidth
              />

              {templateForm.calculationMode === 'pastel_rounded_down' && (
                <>
                  <Typography variant="subtitle2">Rates Configuration</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
                    <TextField size="small" type="number" label="Multiplier" value={templateForm.rates.multiplier} onChange={(e) => updateTemplateRatesField('multiplier', e.target.value)} />
                    <TextField size="small" type="number" label="Water Basic Charge" value={templateForm.rates.waterBasicCharge} onChange={(e) => updateTemplateRatesField('waterBasicCharge', e.target.value)} />
                    <TextField size="small" type="number" label="Sewerage Basic Charge" value={templateForm.rates.sewerBasicCharge} onChange={(e) => updateTemplateRatesField('sewerBasicCharge', e.target.value)} />
                    <TextField size="small" type="number" label="Sewerage Consumption" value={templateForm.rates.sewerConsumptionRate} onChange={(e) => updateTemplateRatesField('sewerConsumptionRate', e.target.value)} />
                  </Box>

                  <Typography variant="subtitle2">Water Consumption Levels</Typography>
                  {(templateForm.rates.waterTiers || []).map((tier, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        label={`Level ${i + 1} Max Range (Litres)`}
                        value={tier.maxRangeLitres}
                        onChange={(e) => updateTemplateTier('waterTiers', i, 'maxRangeLitres', e.target.value)}
                        sx={{ minWidth: 240 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={`Level ${i + 1} Rate`}
                        value={tier.rate}
                        onChange={(e) => updateTemplateTier('waterTiers', i, 'rate', e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        disabled={(templateForm.rates.waterTiers || []).length <= 1}
                        onClick={() => removeTemplateTier('waterTiers', i)}
                      >
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Box>
                    <Button size="small" startIcon={<Add />} onClick={() => addTemplateTier('waterTiers', { maxRangeLitres: 100000000, rate: 0 })}>
                      Add Level
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Use a large max range (e.g. 100000000) for the open-ended top level.
                    </Typography>
                  </Box>

                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Pastel Export Fields</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
                    {[
                      ['gdc', 'GDC'], ['taxType', 'Tax Type'], ['taxAmount', 'Tax Amount'],
                      ['openItem', 'Open Item'], ['projectCode', 'Project Code'],
                      ['contraAccount', 'Contra Account'], ['exchangeRate', 'Exchange Rate'],
                      ['bankExchangeRate', 'Bank Exchange Rate'], ['batchId', 'Batch ID'],
                      ['discountTaxType', 'Discount Tax Type'], ['discountAmount', 'Discount Amount']
                    ].map(([field, label]) => (
                      <TextField
                        key={field}
                        size="small"
                        label={label}
                        value={templateForm.exportConfig[field] ?? ''}
                        onChange={(e) => setTemplateForm((p) => ({
                          ...p,
                          exportConfig: { ...p.exportConfig, [field]: e.target.value }
                        }))}
                      />
                    ))}
                  </Box>
                </>
              )}

              {templateForm.calculationMode === 'tiered' && (
                <>
                  <Typography variant="subtitle2">Pricing Levels (usage thresholds in kL)</Typography>
                  {(templateForm.rates.pricingLevels || []).map((level, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        label={`Level ${i + 1} Threshold (kL)`}
                        value={level.threshold}
                        onChange={(e) => updateTemplateTier('pricingLevels', i, 'threshold', e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={`Level ${i + 1} Rate`}
                        value={level.rate}
                        onChange={(e) => updateTemplateTier('pricingLevels', i, 'rate', e.target.value)}
                        sx={{ minWidth: 160 }}
                      />
                      <TextField
                        size="small"
                        label="Description"
                        value={level.description || ''}
                        onChange={(e) => updateTemplateTier('pricingLevels', i, 'description', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        disabled={(templateForm.rates.pricingLevels || []).length <= 1}
                        onClick={() => removeTemplateTier('pricingLevels', i)}
                      >
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Box>
                    <Button size="small" startIcon={<Add />} onClick={() => addTemplateTier('pricingLevels', { threshold: 0, rate: 0, description: '' })}>
                      Add Level
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={savingTemplateDef}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplateDef}
            disabled={savingTemplateDef}
          >
            {savingTemplateDef ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate template dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => !duplicatingTemplate && setDuplicateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate Template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              label="New Template Key"
              value={duplicateKey}
              onChange={(e) => setDuplicateKey(e.target.value)}
              helperText="Letters, numbers, underscores and hyphens"
            />
            <TextField
              size="small"
              label="New Template Name"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)} disabled={duplicatingTemplate}>Cancel</Button>
          <Button variant="contained" onClick={handleDuplicateTemplate} disabled={duplicatingTemplate}>
            {duplicatingTemplate ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete template dialog */}
      <Dialog open={templateDeleteDialogOpen} onClose={() => !deletingTemplateDef && setTemplateDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template <strong>{templateToDelete?.name}</strong>? Built-in templates will revert to their default rates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDeleteDialogOpen(false)} disabled={deletingTemplateDef}>Cancel</Button>
          <Button onClick={handleConfirmDeleteTemplate} color="error" variant="contained" disabled={deletingTemplateDef}>
            {deletingTemplateDef ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteAccount}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the account <strong>{accountToDelete?.name || 'this account'}</strong>?
            This will remove its user links and unassign its linked sites.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteAccount} disabled={deletingAccount}>Cancel</Button>
          <Button
            onClick={handleConfirmDeleteAccount}
            color="error"
            variant="contained"
            disabled={deletingAccount}
          >
            {deletingAccount ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingReports;
